import { forwardRef, useId, useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';
import { Input, type InputProps, Label, cn } from '@tailoring-crm/ui';

/**
 * The three inputs every auth screen is built from.
 *
 * They exist as a set because the mistakes they prevent are the same on every screen: an
 * address field that autocapitalises the first letter, a password field with no way to check
 * what was typed, and an error message that is red text floating near an input rather than
 * something a screen reader will ever announce.
 */

export interface FieldProps {
  label: string;
  hint?: string;
  error?: string | undefined;
  children?: ReactNode;
}

/** Label, control, hint, error — wired together with ids so assistive tech follows the thread. */
export function Field({
  label,
  hint,
  error,
  render,
}: FieldProps & {
  render: (props: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
}): ReactElement {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy =
    [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ') || undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      {render({ id, describedBy, invalid: error !== undefined })}
      {hint ? (
        <p id={hintId} className="text-sm text-muted-foreground">
          {hint}
        </p>
      ) : null}
      {error ? (
        // `role="alert"` so the message is announced when it appears, not only when the input
        // is next focused.
        <p id={errorId} role="alert" className="text-sm font-medium text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export type EmailFieldProps = Omit<InputProps, 'type'> & {
  label: string;
  hint?: string;
  error?: string | undefined;
};

/**
 * The email address — this product's username.
 *
 * `type="email"` for the keyboard (an `@` and a `.` without switching layers) and
 * `autoComplete="username"` rather than `"email"`, because that is what tells a password
 * manager this field pairs with the password below it. Autocapitalise and autocorrect are both
 * off: a phone helpfully capitalising the first letter of an address is the single most common
 * way a typed address fails to match a stored one.
 */
export const EmailField = forwardRef<HTMLInputElement, EmailFieldProps>(function EmailField(
  { label, hint, error, className, ...props },
  ref,
) {
  return (
    <Field
      label={label}
      {...(hint === undefined ? {} : { hint })}
      error={error}
      render={({ id, describedBy, invalid }) => (
        <Input
          ref={ref}
          id={id}
          type="email"
          inputMode="email"
          autoComplete="username"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          maxLength={254}
          aria-invalid={invalid}
          aria-describedby={describedBy}
          className={className}
          {...props}
        />
      )}
    />
  );
});

export type PasswordFieldProps = Omit<InputProps, 'type'> & {
  label: string;
  hint?: string;
  error?: string | undefined;
};

/**
 * A password with a show/hide toggle.
 *
 * The toggle is not a convenience — on a mobile keyboard a strong password is mistyped often
 * enough that without it people choose a weaker one they can type reliably. It defaults to
 * hidden and never persists across a re-render of the screen.
 */
export const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(
  function PasswordField({ label, hint, error, className, ...props }, ref) {
    const { t } = useTranslation();
    const [visible, setVisible] = useState(false);

    return (
      <Field
        label={label}
        {...(hint === undefined ? {} : { hint })}
        error={error}
        render={({ id, describedBy, invalid }) => (
          <div className="relative">
            <Input
              ref={ref}
              id={id}
              type={visible ? 'text' : 'password'}
              aria-invalid={invalid}
              aria-describedby={describedBy}
              className={cn('pr-12', className)}
              {...props}
            />
            <button
              type="button"
              onClick={() => setVisible((previous) => !previous)}
              aria-label={visible ? t('auth.hidePassword') : t('auth.showPassword')}
              aria-pressed={visible}
              className="absolute inset-y-0 right-0 flex w-12 items-center justify-center rounded-r-md text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {visible ? (
                <EyeOff aria-hidden="true" className="size-5" />
              ) : (
                <Eye aria-hidden="true" className="size-5" />
              )}
            </button>
          </div>
        )}
      />
    );
  },
);

/** A form-level failure: what went wrong, in one place, announced when it appears. */
export function FormError({ message }: { message: string | null }): ReactElement | null {
  if (!message) return null;
  return (
    <p
      role="alert"
      className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive"
    >
      {message}
    </p>
  );
}
